import { useEffect } from 'react';
import {
  persistCanvasPanelSignalKey,
  shouldSuppressCanvasSignalOpen,
} from '../workspace/panels/canvasPanelVisibilityStorage.js';

function text(value) {
  return String(value || '').trim();
}

function visibilityAction(visibility, suppressOpen) {
  const openAction = suppressOpen ? 'record' : 'open';
  return new Map([
    ['closed', 'close'],
    ['open', openAction],
  ]).get(visibility) || 'record';
}

function hasAdmittedTab(canvasTabs, activeTab) {
  if (!Array.isArray(canvasTabs)) {
    return true;
  }
  return canvasTabs.some((tab) => text(Object(tab).key) === activeTab);
}

export function resolveCanvasSignalEffectDecision(options) {
  const {
    canvasTabs,
    latestCanvasSignal,
    lastSignalKey,
    suppressOpen,
  } = {
    latestCanvasSignal: {},
    lastSignalKey: '',
    suppressOpen: false,
    ...options,
  };
  const signalKey = text(latestCanvasSignal.signal_key);
  if (!signalKey || signalKey === lastSignalKey) {
    return { action: 'none', signalKey: '' };
  }
  const action = visibilityAction(latestCanvasSignal.canvas_panel_visibility, suppressOpen);
  if (action === 'close') {
    return { action: 'close', signalKey };
  }
  if (action === 'record') {
    return { action: 'record', signalKey };
  }
  const activeTab = text(latestCanvasSignal.active_tab);
  if (!activeTab) {
    return { action: 'record', signalKey };
  }
  if (!hasAdmittedTab(canvasTabs, activeTab)) {
    return { action: 'none', signalKey: '' };
  }
  return { action: 'open', activeTab, fullscreenOpen: false, signalKey };
}

export function useCanvasSignalEffect({
  canvasTabs = [],
  latestCanvasSignal = {},
  lastCanvasSignalKeyRef,
  setCanvasActiveTabKey,
  setCanvasFullscreenOpen,
  setShowCanvasPanel,
} = {}) {
  useEffect(() => {
    const signalKey = String(latestCanvasSignal.signal_key || '').trim();
    if (!signalKey || signalKey === lastCanvasSignalKeyRef.current) {
      return;
    }
    const decision = resolveCanvasSignalEffectDecision({
      canvasTabs,
      latestCanvasSignal,
      lastSignalKey: '',
      suppressOpen: shouldSuppressCanvasSignalOpen(signalKey),
    });
    if (decision.action === 'none') {
      return;
    }
    lastCanvasSignalKeyRef.current = signalKey;
    persistCanvasPanelSignalKey(signalKey);
    if (decision.action === 'close') {
      setShowCanvasPanel(false);
      setCanvasFullscreenOpen(false);
      return;
    }
    if (decision.action === 'open') {
      setCanvasFullscreenOpen(decision.fullscreenOpen === true);
      setShowCanvasPanel(true);
      setCanvasActiveTabKey(decision.activeTab);
    }
  }, [
    canvasTabs,
    latestCanvasSignal,
    lastCanvasSignalKeyRef,
    setCanvasActiveTabKey,
    setCanvasFullscreenOpen,
    setShowCanvasPanel,
  ]);
}

export default useCanvasSignalEffect;
